import React, { useState, useEffect } from 'react';

const SamHorizontalTimeline = () => {
  const [hoveredActivity, setHoveredActivity] = useState(null);
  const [categoryTotals, setCategoryTotals] = useState({});
  const [contextSwitchingImpact, setContextSwitchingImpact] = useState(0);
  const [workHoursStats, setWorkHoursStats] = useState({
    during: 0,
    outside: 0,
    total: 0,
    outsidePercentage: 0
  });
  
  const activities = [
    { time: '02:00', description: 'Done with work! Time to sleep', category: 'sleep' },
    { time: '03:00', description: 'Excited! Completed model fine-tuning that outperforms benchmarks', category: 'work' },
    { time: '04:00', description: 'Sleep', category: 'sleep' },
    { time: '05:00', description: 'Sleep', category: 'sleep' },
    { time: '06:00', description: 'Sleep', category: 'sleep' },
    { time: '07:00', description: 'Sleep', category: 'sleep' },
    { time: '08:00', description: 'Sleep', category: 'sleep' },
    { time: '09:00', description: 'Wakes up feeling sleepy, coffee solves the issue', category: 'personal' },
    { time: '10:00', description: 'At office 10:15, greeting colleagues before standup', category: 'work' },
    { time: '10:30', description: 'Standup - advises Alice on deployment issues, shares progress & plans to deploy', category: 'meeting' }, // Changed from 'work' to 'meeting'
    { time: '11:00', description: 'Checking chats and messages', category: 'work' },
    { time: '11:30', description: 'Postpones mailbox to verify deployment changes', category: 'work' },
    { time: '11:52', description: 'Distracted by colleagues discussing news', category: 'interruption' },
    { time: '12:05', description: 'Back to deployment validation', category: 'work' },
    { time: '12:23', description: 'Lunch break before completing validation', category: 'break' },
    { time: '13:07', description: 'Shares night work insights with colleagues', category: 'work' },
    { time: '13:15', description: 'Tries to resume work but loses track', category: 'work' },
    { time: '13:23', description: 'Distracted by chat message', category: 'interruption' },
    { time: '13:30', description: 'Coffee break', category: 'break' },
    { time: '13:50', description: 'Back to work', category: 'work' },
    { time: '14:02', description: 'Alice reveals Sam\'s advice was wrong, wasting her 2 hours', category: 'interruption' },
    { time: '14:20', description: 'Back to work', category: 'work' },
    { time: '14:40', description: 'Pushes model deployment', category: 'work' },
    { time: '14:56', description: 'Thinks deployment is green, prepares for meeting, needs fresh air', category: 'work' },
    { time: '15:05', description: 'Meeting with peers from neighboring department', category: 'meeting' },
    { time: '15:53', description: 'Issue with Sam\'s changes, on-call engineer handling it', category: 'issue' },
    { time: '16:17', description: 'Distracted by loud colleagues, loses concentration, starts over', category: 'interruption' },
    { time: '16:54', description: 'Issue mitigated', category: 'work' },
    { time: '17:01', description: 'EM meeting about root cause - Sam missed validation details', category: 'meeting' },
    { time: '17:37', description: 'Coffee break', category: 'break' },
    { time: '18:00', description: 'Reflects on day full of interruptions, disappointed, plans late night work', category: 'personal' },
    { time: '19:00', description: 'Personal time', category: 'personal' },
    { time: '20:00', description: 'Personal time', category: 'personal' },
    { time: '21:00', description: 'Personal time', category: 'personal' },
    { time: '22:18', description: 'Opens laptop for model validation', category: 'work' },
    { time: '23:00', description: 'Working on model validation', category: 'work' },
    { time: '00:00', description: 'Working on model validation', category: 'work' },
    { time: '01:00', description: 'Working on model validation', category: 'work' },
  ];

  const getCategoryColor = (category) => {
    switch(category) {
      case 'sleep': return '#3A3A9B';
      case 'work': return '#4CAF50';
      case 'meeting': return '#F44336';
      case 'break': return '#FF9800';
      case 'personal': return '#9C27B0';
      case 'interruption': return '#FF5722';
      case 'issue': return '#E91E63';
      default: return '#BBBBBB';
    }
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'sleep': return '💤';
      case 'work': return '💻';
      case 'meeting': return '👥';
      case 'break': return '☕';
      case 'personal': return '🏠';
      case 'interruption': return '⚠️';
      case 'issue': return '🔧';
      default: return '⚪';
    }
  };

  // Convert time string (HH:MM) to minutes since midnight
  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Calculate position percentage along the timeline
  const calculatePosition = (timeStr) => {
    const minutes = timeToMinutes(timeStr);
    return (minutes / (24 * 60)) * 100;
  };

  // Calculate width percentage for a time slot
  const calculateWidth = (currentIdx) => {
    const currentTime = activities[currentIdx].time;
    const nextTime = activities[(currentIdx + 1) % activities.length].time;
    
    let currentMinutes = timeToMinutes(currentTime);
    let nextMinutes = timeToMinutes(nextTime);
    
    // Handle wrapping around midnight
    if (nextMinutes < currentMinutes) {
      nextMinutes += 24 * 60;
    }
    
    const duration = nextMinutes - currentMinutes;
    return (duration / (24 * 60)) * 100;
  };
  
  // Calculate total time spent on each category
  useEffect(() => {
    const totals = {};
    let contextSwitchTime = 0;
    let lastCategory = null;
    let workDuringWorkHours = 0;
    let workOutsideWorkHours = 0;
    let meetingTime = 0;
    const workHoursStart = 10 * 60; // 10:00 in minutes
    const workHoursEnd = 18 * 60;   // 18:00 in minutes
    
    activities.forEach((activity, index) => {
      const currentTime = activity.time;
      const nextTime = activities[(index + 1) % activities.length].time;
      
      let currentMinutes = timeToMinutes(currentTime);
      let nextMinutes = timeToMinutes(nextTime);
      
      // Handle wrapping around midnight
      if (nextMinutes < currentMinutes) {
        nextMinutes += 24 * 60;
      }
      
      const duration = nextMinutes - currentMinutes;
      
      // Add to category total
      if (!totals[activity.category]) {
        totals[activity.category] = 0;
      }
      totals[activity.category] += duration;
      
      // Track work hours vs outside work hours
      if (activity.category === 'work' || activity.category === 'meeting') {
        // Track meeting time separately
        if (activity.category === 'meeting') {
          meetingTime += duration;
        }
        
        // Calculate overlap with work hours
        const startInWorkHours = Math.max(currentMinutes, workHoursStart);
        const endInWorkHours = Math.min(nextMinutes, workHoursEnd);
        
        if (startInWorkHours < endInWorkHours) {
          // Some or all of this activity is during work hours
          workDuringWorkHours += (endInWorkHours - startInWorkHours);
        }
        
        // Calculate time outside work hours
        const beforeWorkHours = Math.max(0, Math.min(duration, workHoursStart - currentMinutes));
        const afterWorkHours = Math.max(0, Math.min(duration, nextMinutes - workHoursEnd));
        
        workOutsideWorkHours += (beforeWorkHours + afterWorkHours);
      }
      
      // Track context switching impact - 15 minutes for each interruption
      if (activity.category === 'interruption') {
        contextSwitchTime += 15;
      }
      
      // Category change also costs context switching time
      if (lastCategory && 
          lastCategory !== activity.category && 
          activity.category === 'work' && 
          ['interruption', 'meeting', 'break'].includes(lastCategory)) {
        contextSwitchTime += 15;
      }
      
      lastCategory = activity.category;
    });
    
    // Convert minutes to hours for display
    Object.keys(totals).forEach(category => {
      totals[category] = (totals[category] / 60).toFixed(1);
    });
    
    setCategoryTotals(totals);
    setContextSwitchingImpact((contextSwitchTime / 60).toFixed(1));
    
    // Set work hours stats
    const totalWorkTime = workDuringWorkHours + workOutsideWorkHours;
    setWorkHoursStats({
      during: (workDuringWorkHours / 60).toFixed(1),
      outside: (workOutsideWorkHours / 60).toFixed(1),
      total: (totalWorkTime / 60).toFixed(1),
      outsidePercentage: Math.round((workOutsideWorkHours / totalWorkTime) * 100),
      meetingPercentage: Math.round((meetingTime / totalWorkTime) * 100)
    });
  }, []);

  return (
    <div className="w-full max-w-6xl mx-auto p-4">
      <h1 className="text-2xl font-bold text-center mb-6">Sam's Daily Routine - Microsoft LLM/AI Developer</h1>
      
      {/* Timeline container */}
      <div className="relative w-full h-40 mb-8 overflow-x-auto">
        <div className="absolute top-0 left-0 w-full h-full">
          {/* Hour markers */}
          {Array.from({ length: 25 }).map((_, i) => (
            <div 
              key={`marker-${i}`} 
              className="absolute top-0 h-full border-l border-gray-300" 
              style={{ left: `${(i / 24) * 100}%` }}
            >
              <div className="absolute -left-3 -top-6 text-xs">{i % 24}:00</div>
            </div>
          ))}
          
          {/* Required Work hours indicator */}
          <div 
            className="absolute h-full bg-gray-100 border-l border-r border-gray-300"
            style={{ 
              left: `${(10 / 24) * 100}%`, 
              width: `${(8 / 24) * 100}%`, 
              opacity: 0.3,
              top: 0
            }}
          >
            <div className="absolute top-8 w-full text-center text-xs text-gray-600 font-medium">
              Required Work Hours (10:00 - 18:00)
            </div>
          </div>
          
          {/* Sam's actual working hours - highlighted with stronger color */}
          {activities.map((activity, index) => {
            if (activity.category === 'work' || activity.category === 'meeting') {
              const position = calculatePosition(activity.time);
              const width = calculateWidth(index);
              
              return (
                <div 
                  key={`work-indicator-${index}`}
                  className="absolute border-l border-r border-green-300"
                  style={{ 
                    left: `${position}%`, 
                    width: `${width}%`, 
                    height: '8px',
                    backgroundColor: activity.category === 'work' ? 'rgba(34, 197, 94, 0.8)' : 'rgba(244, 67, 54, 0.8)',
                    top: '28px',
                    zIndex: 5
                  }}
                />
              );
            }
            return null;
          })}
          
          {/* Label for actual working hours */}
          <div className="absolute top-36 w-full text-center text-xs text-green-700 font-medium">
            Sam's Actual Working Hours
          </div>
          
          {/* AM/PM indicators */}
          <div className="absolute top-24 left-0 w-1/2 text-center text-xs text-gray-500">AM</div>
          <div className="absolute top-24 left-1/2 w-1/2 text-center text-xs text-gray-500">PM</div>
          
          {/* Activity blocks */}
          {activities.map((activity, index) => (
            <div 
              key={`activity-${index}`}
              className="absolute h-16 rounded-md border border-gray-400 cursor-pointer transition-all hover:h-20 hover:-top-2"
              style={{
                left: `${calculatePosition(activity.time)}%`,
                width: `${calculateWidth(index)}%`,
                backgroundColor: getCategoryColor(activity.category),
                top: '40px',
              }}
              onMouseEnter={() => setHoveredActivity(activity)}
              onMouseLeave={() => setHoveredActivity(null)}
            >
              <div className="absolute -top-5 left-1 text-xs font-semibold">{activity.time}</div>
              <div className="truncate text-xs p-1 text-white font-medium">
                {getCategoryIcon(activity.category)} {activity.description.length > 20 ? activity.description.substring(0, 20) + '...' : activity.description}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Activity details */}
      <div className="p-4 border rounded shadow-sm mb-6 min-h-32 bg-white">
        <h2 className="text-lg font-semibold">Activity Details</h2>
        {hoveredActivity ? (
          <div>
            <div className="flex items-center">
              <span className="text-2xl mr-2">{getCategoryIcon(hoveredActivity.category)}</span>
              <span className="font-bold">{hoveredActivity.time}</span>
            </div>
            <p className="mt-1">{hoveredActivity.description}</p>
            <p className="capitalize text-sm mt-2 flex items-center">
              <span 
                className="inline-block w-3 h-3 mr-2 rounded-full" 
                style={{backgroundColor: getCategoryColor(hoveredActivity.category)}}
              ></span>
              Category: {hoveredActivity.category}
            </p>
          </div>
        ) : (
          <p className="text-gray-500 italic">Hover over activities to see details</p>
        )}
      </div>
      
      {/* Time Summary */}
      <div className="p-4 border rounded shadow-sm mb-6">
        <h2 className="text-lg font-semibold mb-3">Time Allocation Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(categoryTotals).map(([category, hours]) => (
            <div key={`summary-${category}`} className="flex items-center">
              <div 
                className="w-4 h-4 mr-2 rounded-full" 
                style={{backgroundColor: getCategoryColor(category)}}
              ></div>
              <span className="mr-1">{getCategoryIcon(category)}</span>
              <span className="capitalize font-medium">{category}:</span>
              <span className="ml-1 font-bold">{hours} hrs</span>
            </div>
          ))}
        </div>
        
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <h3 className="font-semibold text-yellow-800">Context Switching Impact</h3>
          <p className="text-sm text-yellow-700">
            Time lost to context switching after interruptions: <span className="font-bold">{contextSwitchingImpact} hours</span>
            <br/>
            <span className="italic">(Assuming 15 minutes to restore context after each interruption or task switch)</span>
          </p>
        </div>
      </div>
      
      {/* Legend and Analysis */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="p-4 border rounded shadow-sm md:w-1/2">
          <h2 className="text-lg font-semibold mb-2">Legend</h2>
          <div className="grid grid-cols-2 gap-2">
            {['sleep', 'work', 'meeting', 'break', 'personal', 'interruption', 'issue'].map(category => (
              <div key={category} className="flex items-center">
                <div 
                  className="w-4 h-4 mr-2 rounded-full" 
                  style={{backgroundColor: getCategoryColor(category)}}
                ></div>
                <span className="mr-1">{getCategoryIcon(category)}</span>
                <span className="capitalize">{category}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="p-4 border rounded shadow-sm md:w-1/2 sticky left-0 top-0">
          <h2 className="text-lg font-semibold mb-2">Sam's Day Analysis</h2>
          <ul className="text-sm list-disc pl-4 space-y-1">
            <li>Works late nights (completing work at 3 AM and 2 AM)</li>
            <li>Only 7 hours of sleep (2 AM - 9 AM)</li>
            <li>{workHoursStats.outsidePercentage}% of work happens outside standard hours (10 AM - 6 PM)</li>
            <li>About {workHoursStats.meetingPercentage}% of productive time spent in meetings (including standups)</li>
            <li>5 interruptions throughout the workday, including chat messages</li>
            <li>Context switching costs approximately {contextSwitchingImpact} hours of productivity</li>
            <li>Late night/early morning work sessions are uninterrupted and likely more productive</li>
            <li>Regular work hours are fragmented by meetings, messages, and interruptions</li>
            <li>Microsoft Teams messages (like at 13:23) break focus and contribute to task switching</li>
            <li>Late night work pattern suggests deadline pressure or avoiding daytime distractions</li>
            <li>Morning standup at 10:30 creates context switching when resuming focused work</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SamHorizontalTimeline;